/**
 * listing.js — which deals the list shows: the status filter and the search
 * =========================================================================
 *
 * Active, Closed, All. Brian, 21 September 2026: "deal reports needs to be
 * edited to also have a toggle for active, closed, all. it should default to
 * active."
 *
 * `Deal.status` is a free-text CharField with a default of "Active", not a
 * choice list, so "Active" and "Closed" are conventions and not a guarantee.
 * The match is case-insensitive and trimmed, and a deal whose status is
 * neither is in no bucket: All shows it and the page names it beside the
 * pills. A row the book holds and no tab shows is CLAUDE.md 0.32's defect.
 * Lead statuses (0.6) are excluded upstream and never reach this page.
 *
 * The search. Brian, 20 October 2026: "Add a search bar at the top as well on
 * deal reports", then "put it here instead" — after the All pill, so the two
 * controls that narrow the list sit together. It reads every field a deal can
 * be named by — name, counterparty, the issuer on its security, ticker and
 * strategy — because a deal is filed under any one of them (0.47): SAFX is
 * "XCF Capital Convertible Note", and NFT Limited's deal carries "MI" as its
 * counterparty, so only the issuer names the company (0.2). Every word of the
 * query has to appear somewhere, in any order. Never a row id and never a
 * date: "25" would match half the book's funding years.
 */

export const strategyLabel = (tag) => tag || "Untagged";

export const DEAL_STATUS_FILTERS = [
  { key: "active", label: "Active" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

export function statusKeyOf(deal) {
  const raw = (deal?.deal?.status || "").trim().toLowerCase();
  if (raw === "active") return "active";
  if (raw === "closed") return "closed";
  return "other";
}

export function filterDealsByStatus(deals, filter) {
  if (filter === "all") return deals;
  return deals.filter((d) => statusKeyOf(d) === filter);
}

const fold = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export const searchWords = (query) => fold(query).split(/\s+/).filter(Boolean);

// `deal` is the record as the API names it: a deal row's `.deal`, or one of a
// strategy's member deals, which carry the same fields bar the strategy — that
// is the group's. An untagged deal reads "Untagged", as its group does, so a
// search for the word lists the deals still to tag.
export function dealSearchText(deal, strategy) {
  const tag = strategy === undefined ? deal?.strategy : strategy;
  return fold(
    [deal?.name, deal?.counterparty, deal?.issuer, deal?.ticker, strategyLabel(tag)]
      .filter(Boolean)
      .join(" · "),
  );
}

export function matchesSearch(text, query) {
  const words = searchWords(query);
  return words.every((w) => text.includes(w));
}

export function searchDeals(deals, query) {
  if (!searchWords(query).length) return deals;
  return deals.filter((d) => matchesSearch(dealSearchText(d.deal), query));
}

// A strategy is listed when its own name matches, or when any deal in it
// does — the second is how somebody finds which group a deal is read under.
// Its figures are never narrowed: the group's curve is every deal of that
// kind, and the table under it still lists them all.
export function searchStrategies(groups, query) {
  if (!searchWords(query).length) return groups;
  return groups.filter(
    (g) =>
      matchesSearch(fold(strategyLabel(g.strategy)), query) ||
      (g.deals || []).some((d) => matchesSearch(dealSearchText(d, g.strategy), query)),
  );
}

// The list's subtitle: how many rows are showing, out of what. With a search
// on it names the search, so a short list does not read as the book.
export function dealListSubtitle({ shown, total, statusFilter, query }) {
  const bucket = statusFilter === "all" ? "" : `${statusFilter} `;
  if (query) {
    const deals = shown === 1 ? "deal matches" : "deals match";
    return `${shown} ${bucket}${deals} “${query}” — of ${total} with cash movement`;
  }
  return statusFilter === "all"
    ? `${shown} with cash movement`
    : `${shown} ${statusFilter} of ${total} with cash movement`;
}

// What an empty list says while a search is on. `elsewhere` is how many deals
// match under any status, so a match on another tab is named rather than
// reading as no match at all.
export function noMatchText({ query, statusFilter, elsewhere }) {
  if (!elsewhere || statusFilter === "all") {
    return `No deal matches “${query}”. The search reads each deal's name, company, issuer, ticker and strategy.`;
  }
  return (
    `No ${statusFilter} deal matches “${query}”. ` +
    `${elsewhere} ${elsewhere === 1 ? "deal does" : "deals do"} under another status.`
  );
}

/* The list as it was left, for the way back.

   Usability review item 22 put a deal on its own page, so leaving the list
   and coming back has to land on the same list: the same filter, search and
   ranking, and the deal you opened marked. It is kept for this browser tab
   only (sessionStorage), so a search left on last week cannot open the page
   on a list with deals missing from it; closing the tab forgets it. The ids
   in the order shown are what the deal page's Previous and Next step
   through. A per-viewer convenience: every access is wrapped, and the page
   works the same without it. */
export const LIST_STATE_KEY = "dealReports.list";

export const DEFAULT_LIST_STATE = {
  mode: "deal",
  statusFilter: "active",
  query: "",
  sort: null,
  strategy: null,
  order: [],
  lastDealId: null,
};

export function readListState() {
  try {
    const raw = window.sessionStorage.getItem(LIST_STATE_KEY);
    if (!raw) return { ...DEFAULT_LIST_STATE };
    const saved = JSON.parse(raw);
    return { ...DEFAULT_LIST_STATE, ...(saved && typeof saved === "object" ? saved : {}) };
  } catch {
    return { ...DEFAULT_LIST_STATE };
  }
}

export function writeListState(patch) {
  const next = { ...readListState(), ...patch };
  try {
    window.sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the list still works, it just forgets on the way back.
  }
  return next;
}

/**
 * The deals either side of one, in the order the list last showed them. A
 * deal opened from a link, or no longer in that order, steps through every
 * deal in the page's own order instead, so Previous and Next always work.
 */
export function neighbours(dealId, savedOrder, fallbackOrder) {
  const id = Number(dealId);
  const order = (savedOrder || []).includes(id) ? savedOrder : fallbackOrder;
  const at = order.indexOf(id);
  if (at < 0) return { previous: null, next: null, position: null, total: order.length, fromList: false };
  return {
    previous: at > 0 ? order[at - 1] : null,
    next: at < order.length - 1 ? order[at + 1] : null,
    position: at + 1,
    total: order.length,
    fromList: order === savedOrder,
  };
}
